import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../api/adminApi";
import type { CopyDashboardResult } from "../../../../../api/types/dashboardsTypes";
import type { SelectedCompany } from "../../hooks/validationStateSlice";
import type { CopyCompany, CopyCompanyDashboard } from "./CopyCompany";
import copyingStateSlice, { actions } from "./copyingStateSlice";

const useDashboardCopying = (companies: SelectedCompany[], copyConditionValues: boolean) => {
  const [state, dispatch] = useReducer(copyingStateSlice.reducer, copyingStateSlice.getInitialState());
  const copyCompaniesRef = useRef<CopyCompany[]>([]);
  const canceledRef = useRef(false);

  const dashboardsToCopy = useMemo(() => {
    return state.companies.flatMap((c) =>
      c.dashboards.filter((r) => r.copying === false && r.copied === false && !r.error)
    );
  }, [state.companies]);

  useEffect(() => {
    dispatch(actions.setCompaniesAction({ companies }));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    copyCompaniesRef.current = state.companies;
  }, [state.companies]);

  const copyDashboard = useCallback(
    (dashboard: CopyCompanyDashboard) => {
      dispatch(actions.updateDashboardAction({ dashboard, changes: { copying: true } }));

      const ct = adminApi.copyDashboard(
        {
          dashboardId: dashboard.dashboard.dashboardId,
          fromCompanyCode: dashboard.dashboard.clientCode,
          toCompanyCode: dashboard.toCompanyCode,
          newDashboardNameUserInput: dashboard.dashboard.nameUserInput,
          copyConditionValues,
          tileIds: dashboard.tiles.map((t) => t.tile.tile.tileId),
        },
        (resp: ApiResponse<CopyDashboardResult>) => {
          const changes: Partial<CopyCompanyDashboard> = { copying: false };
          if (!resp.data?.success || resp.error) {
            changes.error = "An error occurred while copying the dashboard";
            dispatch(actions.updateDashboardAction({ dashboard, changes }));
          } else {
            dispatch(actions.updateCopyResultAction({ dashboard, result: resp.data }));
          }
        },
        () => {
          dispatch(
            actions.updateDashboardAction({
              dashboard,
              changes: { copying: false, copied: false, error: "An error occurred while copying the dashboard" },
            })
          );
        }
      );
      dispatch(actions.updateDashboardAction({ dashboard, changes: { ct } }));
    },
    [copyConditionValues]
  );

  useEffect(() => {
    if (canceledRef.current) return;
    const dashboards = copyCompaniesRef.current.flatMap((c) => c.dashboards);
    const inProgressCount = dashboards.filter((r) => r.copying === true).length;
    const toCopy = dashboards.filter((r) => !r.copying && !r.copied && !r.error);
    if (inProgressCount < 3 && toCopy.length > 0) {
      copyDashboard(defined(toCopy[0]));
    }
  }, [copyDashboard, dashboardsToCopy]);

  const retryCopy = useCallback((dashboard: CopyCompanyDashboard) => {
    dispatch(
      actions.updateDashboardAction({ dashboard, changes: { error: undefined, copied: false, copying: false } })
    );
  }, []);

  const cancel = useCallback(() => {
    canceledRef.current = true;
    copyCompaniesRef.current.forEach((r) => {
      r.dashboards.forEach((r) => {
        if (r.ct) {
          r.ct.cancel();
        }
      });
    });
  }, []);

  return { copying: state.companies, retryCopy, cancel };
};

export default useDashboardCopying;
